import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiCalendar, HiSearch } from 'react-icons/hi';
import { FaPlus } from 'react-icons/fa';
import classNames from 'classnames';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import { Event as CommunityEvent, EventParticipationAnswer } from '../../../../types/event';
import EventsTable from '../../../../components/misc/EventsTable';
import { CommunityRole } from '../../../../types/community';
import { getCommunityUpcomingEvents, getCommunityPastEvents, respondToEvent } from '../../../../services/eventService';
import { getUserId } from '../../../../services/httpService';
import EventDetailModal from '../../../../components/modals/EventDetailModal';
import CommunityHeader from '../../../../components/headers/CommunityHeader';
import CreateEventModal from '../../../../components/modals/CreateEventModal';
import EditEventModal from '../../../../components/modals/EditEventModal';
import HorizontalSponsorsList from '../../../../components/misc/HorizontalSponsorsList';

type EventFilter = 'upcoming' | 'past';

export default function CommunityEventsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCommunity = useSelectedCommunity();
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventFilter, setEventFilter] = useState<EventFilter>('upcoming');
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const canCreateEvents = useMemo(() => {
    if (!selectedCommunity) return false;
    return selectedCommunity.role === CommunityRole.ADMIN || !!selectedCommunity.memberPermissions?.EVENTS?.CREATE;
  }, [selectedCommunity]);

  const canEditEvents = useMemo(() => {
    if (!selectedCommunity) return false;
    return selectedCommunity.role === CommunityRole.ADMIN || !!selectedCommunity.memberPermissions?.EVENTS?.UPDATE;
  }, [selectedCommunity]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editEventId, setEditEventId] = useState<number | null>(null);

  const fetchEvents = async () => {
    if (!selectedCommunity?.id) return;

    setLoading(true);
    try {
      const [upcoming, past] = await Promise.all([
        getCommunityUpcomingEvents(selectedCommunity.id),
        getCommunityPastEvents(selectedCommunity.id),
      ]);
      setEvents([...upcoming, ...past]);
    } catch (error) {
      console.error('Failed to fetch community events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedCommunity?.id]);

  useEffect(() => {
    const state: any = (location && (location.state as any)) || {};
    if (state && state.openEventId) {
      setSelectedEventId(Number(state.openEventId));
      setIsModalOpen(true);
    }
  }, [location.state]);

  const handleRsvpChange = async (eventId: number, answer: EventParticipationAnswer) => {
    const userId = getUserId();
    if (!userId) return;

    setEvents((prevEvents) =>
      prevEvents.map((event) => {
        if (event.id !== eventId) return event;

        const updatedParticipants = event.participants?.map((p) => (p.id === userId ? { ...p, answer } : p)) || [];

        return { ...event, participants: updatedParticipants };
      }),
    );

    try {
      await respondToEvent(eventId.toString(), answer);
    } catch (error) {
      console.error('Failed to update RSVP:', error);
      fetchEvents();
    }
  };

  const handleEventClick = (eventId: number) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
  };

  const filteredEvents = useMemo(() => {
    if (!searchTerm.trim()) return events;

    const lowerSearch = searchTerm.toLowerCase();
    return events.filter(
      (event) =>
        event.title.toLowerCase().includes(lowerSearch) ||
        event.description?.toLowerCase().includes(lowerSearch) ||
        event.location?.toLowerCase().includes(lowerSearch),
    );
  }, [events, searchTerm]);

  const groupedEvents = useMemo(() => {
    const now = new Date();
    const upcoming: CommunityEvent[] = [];
    const past: CommunityEvent[] = [];

    filteredEvents.forEach((event) => {
      const eventDate = new Date(event.startTime);
      if (eventDate >= now) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    upcoming.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    past.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

    return { upcoming, past };
  }, [filteredEvents]);

  const getCurrentUserParticipant = (event: CommunityEvent) => {
    const userId = getUserId();
    if (!userId) return undefined;
    return event.participants?.find((p) => p.id === userId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-details">{t('general.loading')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <CommunityHeader
        title={t('page.community.tabs.events')}
        action={
          canCreateEvents ? (
            <div className="w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                aria-label={t('page.dashboard.events.create')}
                className="mt-1 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-opacity-90 sm:mt-0">
                <FaPlus />
                <span className="truncate">{t('page.dashboard.events.create')}</span>
              </button>
            </div>
          ) : undefined
        }
      />

      <HorizontalSponsorsList placement="EVENTS" />

      <>
        {/* Search Bar and Filter Buttons */}
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <HiSearch className="h-5 w-5 text-primary" />
            </div>
            <input
              type="search"
              placeholder={t('page.dashboard.event.searchEvents')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg !bg-secondary-50 py-2 pl-10 pr-4 text-primary focus:outline-none focus:ring-0 sm:w-1/3"
            />
          </div>
          <div className="flex w-full flex-nowrap items-center justify-start gap-1 rounded-lg bg-secondary-50 p-1 sm:w-auto sm:justify-end">
            <button
              type="button"
              onClick={() => setEventFilter('upcoming')}
              className={classNames(
                'flex-1 rounded-md px-3 py-1 text-center text-sm font-medium text-primary transition-all sm:flex-none sm:px-4 sm:py-2 sm:text-base',
                {
                  '!bg-secondary': eventFilter === 'upcoming',
                },
              )}>
              {t('page.dashboard.event.upcomingEvents')}
            </button>
            <button
              type="button"
              onClick={() => setEventFilter('past')}
              className={classNames(
                'flex-1 rounded-md px-3 py-1 text-center text-sm font-medium text-primary transition-all sm:flex-none sm:px-4 sm:py-2 sm:text-base',
                {
                  '!bg-secondary': eventFilter === 'past',
                },
              )}>
              {t('page.dashboard.event.pastEvents')}
            </button>
          </div>
        </div>
      </>

      {filteredEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <HiCalendar className="h-16 w-16 text-primary" />
          <h3 className="text-xl font-semibold text-primary">{t('page.community.empty.events')}</h3>
          {canCreateEvents && (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-6 py-2 text-white transition-colors hover:bg-opacity-90">
              <FaPlus />
              {t('page.dashboard.events.create')}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Upcoming Events */}
          {eventFilter === 'upcoming' && groupedEvents.upcoming.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <HiCalendar className="h-16 w-16 text-primary" />
              <h3 className="text-xl font-semibold text-primary">{t('page.community.empty.events')}</h3>
              {canCreateEvents && (
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-6 py-2 text-white transition-colors hover:bg-opacity-90">
                  <FaPlus />
                  {t('page.dashboard.events.create')}
                </button>
              )}
            </div>
          )}
          {eventFilter === 'upcoming' && groupedEvents.upcoming.length > 0 && (
            <EventsTable
              events={groupedEvents.upcoming}
              onRowClick={handleEventClick}
              onEdit={(id) => {
                setEditEventId(id);
                setShowEditModal(true);
              }}
              canEdit={canEditEvents}
              onRsvpChange={(id, answer) => handleRsvpChange(id, answer)}
              getCurrentUserParticipant={getCurrentUserParticipant}
              onBuyTicket={(id) => navigate(`/event/invite`, { state: { eventId: id } })}
            />
          )}

          {/* Past Events */}
          {eventFilter === 'past' && groupedEvents.past.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <HiCalendar className="h-16 w-16 text-primary" />
              <h3 className="text-xl font-semibold text-primary">{t('page.community.empty.events')}</h3>
              {canCreateEvents && (
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-6 py-2 text-white transition-colors hover:bg-opacity-90">
                  <FaPlus />
                  {t('page.dashboard.events.create')}
                </button>
              )}
            </div>
          )}
          {eventFilter === 'past' && groupedEvents.past.length > 0 && (
            <EventsTable
              events={groupedEvents.past}
              onRowClick={handleEventClick}
              onEdit={(id) => {
                setEditEventId(id);
                setShowEditModal(true);
              }}
              canEdit={canEditEvents}
              onRsvpChange={(id, answer) => handleRsvpChange(id, answer)}
              getCurrentUserParticipant={getCurrentUserParticipant}
              onBuyTicket={(id) => navigate(`/event/invite`, { state: { eventId: id } })}
            />
          )}
        </>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal open={isModalOpen} setOpen={setIsModalOpen} eventId={selectedEventId} />

      {/* Create Event Modal */}
      <CreateEventModal open={showCreateModal} setOpen={setShowCreateModal} onSuccess={fetchEvents} />

      {/* Edit Event Modal */}
      <EditEventModal
        open={showEditModal}
        setOpen={setShowEditModal}
        eventId={editEventId}
        onSuccess={() => {
          setShowEditModal(false);
          fetchEvents();
          setIsModalOpen(false);
        }}
      />
    </div>
  );
}
